import React, { useMemo } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { useManagementCompanyLogoBlobUrl } from '../../wrh/reports/useManagementCompanyLogo';
import type { CrewVesselListResponse } from '../types';
import { CrewWagesListReport } from './CrewWagesListReport';
import { CREW_LIST_PRINT_META, isCrewListPrintKey } from './crewListPrintMeta';
import { ImoCrewListReport } from './ImoCrewListReport';
import { OnboardCrewListReport } from './OnboardCrewListReport';

function buildListBackPath(params: URLSearchParams): string {
  const vesselId = params.get('vesselId')?.trim() ?? '';
  const periodFrom = params.get('periodFrom')?.trim() ?? '';
  const periodTo = params.get('periodTo')?.trim() ?? '';
  if (!vesselId || !periodFrom || !periodTo) return '/crew/list';
  const qs = new URLSearchParams({ vesselId, periodFrom, periodTo });
  return `/crew/list?${qs.toString()}`;
}

const CrewListPrintPage: React.FC = () => {
  const { colors } = useTheme();
  const { key: keyParam } = useParams<{ key: string }>();
  const [searchParams] = useSearchParams();
  const key = isCrewListPrintKey(keyParam) ? keyParam : null;

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const periodFrom = searchParams.get('periodFrom')?.trim() ?? '';
  const periodTo = searchParams.get('periodTo')?.trim() ?? '';
  const portName = searchParams.get('port')?.trim() || null;
  const arrivalDate = searchParams.get('arrivalDate')?.trim() || null;
  const lastPort = searchParams.get('lastPort')?.trim() || null;
  const voyageNumber = searchParams.get('voyage')?.trim() || null;
  const isArrival = searchParams.get('arrival') === '1';
  const isDeparture = searchParams.get('departure') === '1';

  const listUrl =
    vesselId && periodFrom && periodTo
      ? `/crew/vessel-list?vesselId=${encodeURIComponent(vesselId)}&periodFrom=${encodeURIComponent(periodFrom)}&periodTo=${encodeURIComponent(periodTo)}`
      : null;

  const { data, error, isLoading } = useSWR<CrewVesselListResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const managementCompany = useMemo(() => {
    const manager = data?.vessel?.manager;
    if (manager?.id) {
      return {
        id: manager.id,
        name: manager.name,
        logoStorageKey: manager.logoStorageKey ?? null,
      };
    }
    return null;
  }, [data?.vessel?.manager]);

  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    managementCompany?.id,
    managementCompany?.logoStorageKey,
  );

  const backTo = buildListBackPath(searchParams);

  if (!key) {
    return <Navigate to="/crew/list" replace />;
  }

  if (!vesselId || !periodFrom || !periodTo) {
    return <Navigate to="/crew/list" replace />;
  }

  const meta = CREW_LIST_PRINT_META[key];

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading {meta.title}…
      </div>
    );
  }

  if (error || !data?.vessel) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load vessel crew list.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Crew List
        </Link>
      </div>
    );
  }

  const subtitle = `${data.vessel.vesselName} · ${periodFrom}${
    periodFrom !== periodTo ? ` – ${periodTo}` : ''
  }`;

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Crew List"
      title={meta.title}
      subtitle={subtitle}
      printPageSize={meta.printPageSize}
      printMargin="0"
    >
      {key === 'imo' ? (
        <ImoCrewListReport
          data={data}
          companyLogoSrc={companyLogoSrc}
          companyName={managementCompany?.name}
          portName={portName}
          arrivalDate={arrivalDate}
          lastPort={lastPort}
          voyageNumber={voyageNumber}
          isArrival={isArrival}
          isDeparture={isDeparture}
        />
      ) : null}
      {key === 'onboard' ? (
        <OnboardCrewListReport
          data={data}
          companyLogoSrc={companyLogoSrc}
          companyName={managementCompany?.name}
        />
      ) : null}
      {key === 'wages' ? (
        <CrewWagesListReport
          data={data}
          companyLogoSrc={companyLogoSrc}
          companyName={managementCompany?.name}
        />
      ) : null}
    </ReportView>
  );
};

export default CrewListPrintPage;
